import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBox, Loading } from '../components/Feedback';
import { QuantityControl } from '../components/QuantityControl';
import { api } from '../lib/api';
import { clientUuid } from '../lib/id';
import type { StockItem, StockReturnRequest } from '../types';

type StockTab = 'usable' | 'defective' | 'returns';
const returnStatusLabels: Record<string, string> = {
  RETUR_SOLICITAT: 'Așteaptă validare depozit',
  RETURNAT_DEPOZIT: 'Recepționat',
  RESPINS_DEPOZIT: 'Respins',
  ANULAT_TEHNICIAN: 'Anulat',
};

export function StockPage() {
  const [tab, setTab] = useState<StockTab>('usable');
  const [usable, setUsable] = useState<StockItem[]>([]);
  const [defective, setDefective] = useState<StockItem[]>([]);
  const [returns, setReturns] = useState<StockReturnRequest[]>([]);
  const [search, setSearch] = useState('');
  const [returnMode, setReturnMode] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const operationId = useRef<string | null>(null);
  const debounceTimer = useRef<number | null>(null);
  const items = tab === 'usable' ? usable : tab === 'defective' ? defective : [];
  const itemKey = (item: StockItem) => item.stockItemId || item.partId;

  const load = useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    Promise.all([api.stock(), api.defectStock(), api.stockReturns()])
      .then(([usableRows, defectiveRows, returnRows]) => {
        setUsable(usableRows);
        setDefective(defectiveRows);
        setReturns(returnRows);
        setError('');
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Stocul nu poate fi încărcat.'))
      .finally(() => { if (!quiet) setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const refresh = () => {
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      debounceTimer.current = window.setTimeout(() => load(true), 250);
    };
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('nova:stock-change', refresh);
    window.addEventListener('nova:stock-return-change', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', visible);
    return () => {
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      window.removeEventListener('nova:stock-change', refresh);
      window.removeEventListener('nova:stock-return-change', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [load]);

  const filtered = useMemo(() => {
    const value = search.trim().toLocaleLowerCase('ro');
    return !value ? items : items.filter((item) => `${item.partCode} ${item.partName}`.toLocaleLowerCase('ro').includes(value));
  }, [items, search]);
  const selected = items.filter((item) => (quantities[itemKey(item)] || 0) > 0).map((item) => ({
    partId: item.partId,
    partCode: item.partCode,
    partName: item.partName,
    qty: Number(quantities[itemKey(item)] || 0),
    subunitateId: item.subunitateId,
    warehouseId: item.warehouseId,
  }));
  const total = items.reduce((sum, item) => sum + Number(item.physicalQty ?? item.qty ?? 0), 0);
  const pendingTotal = items.reduce((sum, item) => sum + Number(item.pendingReturnQty || 0), 0);
  const low = tab === 'usable' ? items.filter((item) => item.minQty !== undefined && item.qty <= Number(item.minQty)).length : 0;

  function changeTab(next: StockTab) {
    setTab(next);
    setReturnMode(false);
    setQuantities({});
    operationId.current = null;
  }

  async function submitReturn() {
    setBusy(true);
    setError('');
    try {
      operationId.current ||= `fsm:return:${clientUuid()}`;
      await api.stockReturnRequest(tab === 'defective' ? 'defective' : 'good', selected, `Retur piese ${tab === 'defective' ? 'defecte' : 'bune'} din aplicația mobilă`, operationId.current);
      operationId.current = null;
      setReturnMode(false);
      setQuantities({});
      load();
    } catch (reason) {
      // Cheia ramane neschimbata pentru retry dupa timeout sau dublu click.
      setError(reason instanceof Error ? reason.message : 'Returul nu a putut fi înregistrat.');
    } finally { setBusy(false); }
  }

  async function cancelReturn(request: StockReturnRequest) {
    setBusy(true);
    setError('');
    try {
      await api.cancelStockReturn(request.id);
      load(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Cererea nu a putut fi anulată.');
    } finally { setBusy(false); }
  }

  return <div className="page">
    <header className="simple-header stock-header"><div><small>Gestiunea personală</small><h1>Stocul meu</h1></div><Link className="btn btn-secondary" to="/stock/transfer">Transfer</Link></header>
    <section className={`page-body ${returnMode ? 'with-cta' : ''}`}>
      <div className="stock-kind-tabs">
        <button className={tab === 'usable' ? 'active' : ''} onClick={() => changeTab('usable')}>Piese bune</button>
        <button className={tab === 'defective' ? 'active' : ''} onClick={() => changeTab('defective')}>Piese defecte</button>
        <button className={tab === 'returns' ? 'active' : ''} onClick={() => changeTab('returns')}>Cereri retur</button>
      </div>
      {tab !== 'returns' && <>
        <div className="metric-grid"><div><strong>{items.length}</strong><span>Tipuri piese</span></div><div><strong>{total}</strong><span>Total fizic</span></div><div className={pendingTotal > 0 ? 'warning' : ''}><strong>{pendingTotal}</strong><span>Așteaptă depozit</span></div></div>
        {low > 0 && <div className="alert">Unele piese au ajuns la stocul minim.</div>}
        <div className="stock-toolbar"><input className="search" type="search" placeholder="Caută piesă sau cod…" value={search} onChange={(event) => setSearch(event.target.value)} /><button className="btn btn-secondary" onClick={() => { setReturnMode((value) => !value); setQuantities({}); operationId.current = null; }}>{returnMode ? 'Renunță' : 'Solicită retur'}</button></div>
      </>}
      {loading && <Loading />}{error && <ErrorBox message={error} />}
      {tab !== 'returns' && <div className="stock-list">{filtered.map((item) => {
        const available = Number(item.availableReturnQty ?? item.qty ?? 0);
        const pending = Number(item.pendingReturnQty || 0);
        return <article key={itemKey(item)} className="stock-row"><div><small>{item.partCode}</small><strong>{item.partName}</strong>{item.minQty !== undefined && <span className="muted">Minim: {item.minQty}</span>}{pending > 0 && <div className="pending-return-summary"><span className="status-badge status-retur_solicitat">Așteaptă validare depozit</span><span>Cantitate în așteptare: <b>{pending}</b> · disponibilă pentru retur: <b>{available}</b></span></div>}</div>{returnMode ? <QuantityControl value={quantities[itemKey(item)] || 0} max={available} onChange={(value) => { operationId.current = null; setQuantities((current) => ({ ...current, [itemKey(item)]: value })); }} /> : <b>{item.physicalQty ?? item.qty}</b>}</article>;
      })}</div>}
      {tab === 'returns' && <div className="stock-list return-request-list">{returns.map((request) => <article key={request.id} className="stock-row return-request-row"><div><small>{request.kind === 'defective' ? 'Piesă defectă' : 'Piesă bună'} · {request.code}</small><strong>{request.name}</strong><span className="muted">Cantitate: {request.quantity} · {request.createdAt ? new Date(request.createdAt).toLocaleString('ro-RO') : '—'}</span>{request.rejectionReason && <span className="muted">Motiv: {request.rejectionReason}</span>}</div><div className="return-request-actions"><span className={`status-badge status-${request.state.toLowerCase()}`}>{returnStatusLabels[request.state] || request.state}</span>{request.state === 'RETUR_SOLICITAT' && <button className="btn btn-secondary" disabled={busy} onClick={() => void cancelReturn(request)}>Anulează</button>}</div></article>)}</div>}
      {!loading && tab !== 'returns' && !filtered.length && <div className="empty"><strong>{tab === 'defective' ? 'Nu există piese defecte în gestiune.' : 'Nu există piese în gestiune.'}</strong>{tab === 'usable' && <Link className="btn btn-secondary block" to="/stock/transfer">Transferă din depozit</Link>}</div>}
      {!loading && tab === 'returns' && !returns.length && <div className="empty"><strong>Nu există cereri de retur.</strong></div>}
    </section>
    {returnMode && <div className="cta"><button className="btn btn-primary block" disabled={!selected.length || busy} onClick={() => void submitReturn()}>{busy ? 'Se înregistrează…' : `Solicită returul (${selected.reduce((sum, item) => sum + Number(item.qty || 0), 0)})`}</button></div>}
  </div>;
}
